/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { collection, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";

function hasValidCoordinates(location) {
  return (
    location?.lat != null &&
    location?.lng != null &&
    Number.isFinite(Number(location.lat)) &&
    Number.isFinite(Number(location.lng))
  );
}

function LocationPickerModal({ initialLocation, onClose, onConfirm }) {
  const [selectedLocation, setSelectedLocation] = useState(() =>
    hasValidCoordinates(initialLocation)
      ? {
          ...initialLocation,
          lat: Number(initialLocation.lat),
          lng: Number(initialLocation.lng),
        }
      : null,
  );

  const mapContainer = useRef(null);
  const searchInputRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const [locating, setLocating] = useState(false);
  const [recentLocations, setRecentLocations] = useState([]);
  const [mapType, setMapType] = useState("roadmap");

  useEffect(() => {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    const html = document.documentElement;
    const body = document.body;
    const appRoot = document.getElementById("root");

    const previousHtmlOverflow = html.style.overflow;
    const previousHtmlOverscroll = html.style.overscrollBehavior;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyOverscroll = body.style.overscrollBehavior;

    const previousRootPosition = appRoot?.style.position || "";
    const previousRootTop = appRoot?.style.top || "";
    const previousRootLeft = appRoot?.style.left || "";
    const previousRootWidth = appRoot?.style.width || "";

    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";

    if (appRoot) {
      appRoot.style.position = "fixed";
      appRoot.style.top = `-${scrollY}px`;
      appRoot.style.left = `-${scrollX}px`;
      appRoot.style.width = "100%";
    }

    return () => {
      html.style.overflow = previousHtmlOverflow;
      html.style.overscrollBehavior = previousHtmlOverscroll;
      body.style.overflow = previousBodyOverflow;
      body.style.overscrollBehavior = previousBodyOverscroll;

      if (appRoot) {
        appRoot.style.position = previousRootPosition;
        appRoot.style.top = previousRootTop;
        appRoot.style.left = previousRootLeft;
        appRoot.style.width = previousRootWidth;
      }

      window.scrollTo(scrollX, scrollY);
    };
  }, []);

  function loadGoogleMapsScript() {
    return new Promise((resolve, reject) => {
      if (window.google?.maps?.places) {
        resolve();
        return;
      }

      const existingScript = document.getElementById("google-maps-script");

      if (existingScript) {
        const waitForGoogleMaps = () => {
          if (window.google?.maps?.places) {
            resolve();
            return;
          }

          window.setTimeout(waitForGoogleMaps, 50);
        };

        existingScript.addEventListener("error", reject, { once: true });
        waitForGoogleMaps();
        return;
      }

      const callbackName = "__bioindexGoogleMapsReady";

      window[callbackName] = () => {
        delete window[callbackName];
        resolve();
      };

      const script = document.createElement("script");
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&loading=async&libraries=places&callback=${callbackName}`;
      script.async = true;

      script.onerror = (error) => {
        delete window[callbackName];
        reject(error);
      };

      document.head.appendChild(script);
    });
  }

  function getAddressComponent(components, type) {
    const match = components?.find((c) => c.types.includes(type));

    return match?.long_name || match?.longText || "";
  }

  function cleanGoogleLocationName(name) {
    return (name || "")
      .replace(/,\s*[A-Z0-9]{4}\+[A-Z0-9]{2,4}/g, "")
      .replace(/,\s*\d{4,8}(?=,|$)/g, "")
      .replace(/\s{2,}/g, " ")
      .replace(/,\s*,/g, ",")
      .trim();
  }

  function buildLocationData({ name, coords, place }) {
    const components =
      place?.address_components || place?.addressComponents || [];

    return {
      name: name || "Selected location",
      lat: coords.lat,
      lng: coords.lng,
      country: getAddressComponent(components, "country"),
      adminArea: getAddressComponent(components, "administrative_area_level_1"),
      city:
        getAddressComponent(components, "locality") ||
        getAddressComponent(components, "administrative_area_level_2"),
      placeId: place?.place_id || place?.id || "",
    };
  }

  function getLocationTime(form) {
    const createdAt = form?.createdAt;

    if (createdAt?.toMillis) return createdAt.toMillis();
    if (createdAt instanceof Date) return createdAt.getTime();
    if (typeof createdAt === "number") return createdAt;

    return 0;
  }

  function getLocationKey(location) {
    if (location.placeId) return `place:${location.placeId}`;

    return `coords:${Number(location.lat).toFixed(5)},${Number(
      location.lng,
    ).toFixed(5)}`;
  }

  async function loadRecentMappedLocations() {
    if (!auth.currentUser) return;

    const speciesSnap = await getDocs(
      collection(db, "users", auth.currentUser.uid, "species"),
    );

    const mappedLocations = [];

    speciesSnap.forEach((speciesDoc) => {
      const data = speciesDoc.data();
      const forms = Array.isArray(data.forms) ? data.forms : [];

      forms.forEach((form) => {
        if (form.locationLat == null || form.locationLng == null) return;

        mappedLocations.push({
          name: form.locationName || form.location || "Mapped location",
          lat: form.locationLat,
          lng: form.locationLng,
          country: form.locationCountry || "",
          adminArea: form.locationAdminArea || "",
          city: form.locationCity || "",
          placeId: form.locationPlaceId || "",
          createdAtMs: getLocationTime(form),
        });
      });
    });

    const uniqueLocations = [];

    mappedLocations
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
      .forEach((location) => {
        const key = getLocationKey(location);

        if (uniqueLocations.some((existing) => existing.key === key)) return;

        uniqueLocations.push({
          ...location,
          key,
        });
      });

    setRecentLocations(uniqueLocations.slice(0, 20));
  }

  function selectRecentLocation(location) {
    const coords = {
      lat: location.lat,
      lng: location.lng,
    };

    if (mapRef.current) {
      mapRef.current.panTo(coords);
      mapRef.current.setZoom(14);
    }

    setMarker(coords);

    setSelectedLocation({
      name: location.name,
      lat: location.lat,
      lng: location.lng,
      country: location.country || "",
      adminArea: location.adminArea || "",
      city: location.city || "",
      placeId: location.placeId || "",
    });
  }

  useEffect(() => {
    let isMounted = true;

    async function initializeMap() {
      await loadGoogleMapsScript();

      if (!isMounted || !mapContainer.current) return;

      const initialCoordinates = hasValidCoordinates(initialLocation)
        ? {
            lat: Number(initialLocation.lat),
            lng: Number(initialLocation.lng),
          }
        : null;

      const center = initialCoordinates || {
        lat: 6.2518,
        lng: -75.5636,
      };

      const map = new window.google.maps.Map(mapContainer.current, {
        center,
        zoom: initialCoordinates ? 14 : 10,
        disableDefaultUI: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: false,
        cameraControl: false,
        rotateControl: false,
        scaleControl: false,
      });

      mapRef.current = map;
      geocoderRef.current = new window.google.maps.Geocoder();

      if (initialCoordinates) {
        setMarker(initialCoordinates);
      }

      map.addListener("click", (e) => {
        const coords = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };

        setMarker(coords);

        geocoderRef.current.geocode({ location: coords }, (results, status) => {
          const reverseResults = status === "OK" ? results || [] : [];

          findNearbyPlaceName(coords, reverseResults, ({ name, place }) => {
            setSelectedLocation(
              buildLocationData({
                name,
                coords,
                place,
              }),
            );
          });
        });
      });

      const autocomplete = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        {
          fields: [
            "address_components",
            "formatted_address",
            "geometry",
            "name",
            "place_id",
          ],
        },
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();

        if (!place.geometry?.location) return;

        const coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        map.panTo(coords);
        map.setZoom(14);

        setMarker(coords);

        setSelectedLocation(
          buildLocationData({
            name: cleanGoogleLocationName(
              [
                place.name,
                getAddressComponent(place.address_components, "locality") ||
                  getAddressComponent(
                    place.address_components,
                    "administrative_area_level_2",
                  ),
                getAddressComponent(
                  place.address_components,
                  "administrative_area_level_1",
                ),
                getAddressComponent(place.address_components, "country"),
              ]
                .filter(Boolean)
                .filter((v, i, arr) => arr.indexOf(v) === i)
                .join(", ") ||
                place.formatted_address ||
                "Selected place",
            ),
            coords,
            place,
          }),
        );
      });
    }

    initializeMap();
    loadRecentMappedLocations();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setMarker(coords) {
    if (!mapRef.current) return;

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position: coords,
        map: mapRef.current,
      });
    } else {
      markerRef.current.setPosition(coords);
    }
  }

  function findNearbyPlaceName(coords, reverseResults, callback) {
    const service = new window.google.maps.places.PlacesService(mapRef.current);

    const regionalPlace =
      reverseResults.find((r) =>
        r.types?.some((type) =>
          [
            "locality",
            "administrative_area_level_2",
            "administrative_area_level_1",
            "country",
          ].includes(type),
        ),
      ) ||
      reverseResults.find((r) => r.address_components?.length > 0) ||
      null;

    function buildRegionalName(place) {
      const components = place?.address_components || [];

      const locality =
        getAddressComponent(components, "locality") ||
        getAddressComponent(components, "administrative_area_level_2");

      const admin = getAddressComponent(
        components,
        "administrative_area_level_1",
      );

      const country = getAddressComponent(components, "country");

      return cleanGoogleLocationName(
        [locality, admin, country]
          .filter(Boolean)
          .filter((v, i, arr) => arr.indexOf(v) === i)
          .join(", "),
      );
    }

    service.nearbySearch(
      {
        location: coords,
        radius: 300,
      },
      (places, status) => {
        function getDistance(a, b) {
          const R = 6371000;
          const dLat = ((b.lat - a.lat) * Math.PI) / 180;
          const dLng = ((b.lng - a.lng) * Math.PI) / 180;

          const lat1 = (a.lat * Math.PI) / 180;
          const lat2 = (b.lat * Math.PI) / 180;

          const x =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLng / 2) *
              Math.sin(dLng / 2) *
              Math.cos(lat1) *
              Math.cos(lat2);

          const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
          return R * c;
        }

        const nearestPlace =
          status === window.google.maps.places.PlacesServiceStatus.OK
            ? places
                ?.filter((p) => p.geometry?.location)
                .map((p) => ({
                  ...p,
                  distance: getDistance(coords, {
                    lat: p.geometry.location.lat(),
                    lng: p.geometry.location.lng(),
                  }),
                }))
                .sort((a, b) => a.distance - b.distance)[0]
            : null;

        if (nearestPlace?.place_id && nearestPlace.distance < 200) {
          service.getDetails(
            {
              placeId: nearestPlace.place_id,
              fields: [
                "address_components",
                "formatted_address",
                "geometry",
                "name",
                "place_id",
              ],
            },
            (placeDetails, detailsStatus) => {
              const details =
                detailsStatus ===
                window.google.maps.places.PlacesServiceStatus.OK
                  ? placeDetails
                  : null;

              const regionalName =
                buildRegionalName(details) || buildRegionalName(regionalPlace);

              const name = cleanGoogleLocationName(
                [details?.name || nearestPlace.name, regionalName]
                  .filter(Boolean)
                  .filter((v, i, arr) => arr.indexOf(v) === i)
                  .join(", "),
              );

              callback({
                name:
                  name ||
                  details?.formatted_address ||
                  regionalName ||
                  "Unknown location",
                place: details || regionalPlace,
              });
            },
          );

          return;
        }

        const regionalName = buildRegionalName(regionalPlace);

        if (regionalName || regionalPlace?.formatted_address) {
          callback({
            name:
              regionalName ||
              cleanGoogleLocationName(regionalPlace?.formatted_address),
            place: regionalPlace,
          });

          return;
        }

        function getComponentFromAnyResult(type) {
          for (const result of reverseResults) {
            const value = getAddressComponent(result.address_components, type);
            if (value) return value;
          }

          return "";
        }

        const locality =
          getComponentFromAnyResult("locality") ||
          getComponentFromAnyResult("administrative_area_level_2");

        const admin = getComponentFromAnyResult("administrative_area_level_1");

        const country = getComponentFromAnyResult("country");

        const politicalName = cleanGoogleLocationName(
          [locality, admin, country]
            .filter(Boolean)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .join(", "),
        );

        callback({
          name: politicalName || "Unknown location",
          place:
            reverseResults.find((r) => r.address_components?.length > 0) ||
            regionalPlace,
        });
      },
    );
  }

  function toggleMapType() {
    const nextType = mapType === "roadmap" ? "satellite" : "roadmap";

    setMapType(nextType);

    if (mapRef.current) {
      mapRef.current.setMapTypeId(nextType);
    }
  }

  async function useCurrentLocation() {
    if (!mapRef.current || !geocoderRef.current) return;

    setLocating(true);

    try {
      const options = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000,
      };

      let position;

      if (Capacitor.isNativePlatform()) {
        const permissions = await Geolocation.requestPermissions();

        const hasLocationPermission =
          permissions.location === "granted" ||
          permissions.coarseLocation === "granted";

        if (!hasLocationPermission) {
          throw new Error("LOCATION_PERMISSION_DENIED");
        }

        position = await Geolocation.getCurrentPosition(options);
      } else {
        if (!navigator.geolocation) {
          throw new Error("GEOLOCATION_UNAVAILABLE");
        }

        position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, options);
        });
      }

      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      mapRef.current.panTo(coords);
      mapRef.current.setZoom(15);
      setMarker(coords);

      geocoderRef.current.geocode({ location: coords }, (results, status) => {
        const reverseResults = status === "OK" ? results || [] : [];

        findNearbyPlaceName(coords, reverseResults, ({ name, place }) => {
          setSelectedLocation(buildLocationData({ name, coords, place }));
          setLocating(false);
        });
      });
    } catch (error) {
      console.error("Location error:", error);
      setLocating(false);

      if (
        error?.message === "LOCATION_PERMISSION_DENIED" ||
        error?.code === "OS-PLUG-GLOC-0003" ||
        error?.code === 1
      ) {
        alert(
          "Location permission was denied. Allow BioIndex to access your location and try again.",
        );
      } else if (
        error?.code === "OS-PLUG-GLOC-0007" ||
        error?.code === "OS-PLUG-GLOC-0017"
      ) {
        alert("Turn on Location on your phone and try again.");
      } else if (error?.code === "OS-PLUG-GLOC-0010" || error?.code === 3) {
        alert("Your location could not be determined in time. Try again.");
      } else {
        alert("Could not access your current location.");
      }
    }
  }

  return (
    <div className="fixed inset-x-0 top-0 h-dvh overflow-hidden overscroll-none bg-black/50 flex items-stretch justify-center z-50 sm:inset-0 sm:items-center sm:px-4 sm:py-4">
      <div className="bg-white w-screen h-full min-h-0 overflow-y-auto overscroll-contain sm:w-full sm:h-auto sm:overflow-y-visible sm:max-w-2xl sm:rounded-3xl p-4 sm:p-5 shadow-xl border border-[#93ca87]/40 flex flex-col">
        <div className="shrink-0 border border-[#374049]/15 bg-[#374049] p-3 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-3">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search for a location"
              className="w-full rounded-xl border border-[#93ca87]/40 bg-white px-4 py-3 text-sm text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
            />

            <select
              value=""
              onChange={(e) => {
                const location = recentLocations.find(
                  (recentLocation) => recentLocation.key === e.target.value,
                );

                if (location) selectRecentLocation(location);
              }}
              disabled={recentLocations.length === 0}
              className="w-full rounded-xl border border-white/20 bg-white px-3 py-3 text-sm text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25 disabled:text-slate-400"
            >
              <option value="">Recent locations</option>

              {recentLocations.map((location) => (
                <option key={location.key} value={location.key}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="relative w-full flex-1 min-h-0 sm:flex-none sm:h-[400px] overflow-hidden border-2 border-[#374049]">
          {" "}
          <div ref={mapContainer} className="w-full h-full" />
          <div className="absolute right-2 bottom-6 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={toggleMapType}
              title="Toggle map type"
              aria-label="Toggle map type"
              className="relative h-10 w-20 rounded-full bg-white border border-[#93ca87]/40 shadow-md transition"
            >
              <span
                className={`absolute top-1 left-1 h-8 w-8 rounded-full bg-[#24c3be] transition-transform ${
                  mapType === "satellite" ? "translate-x-10" : "translate-x-0"
                }`}
              />

              <span className="relative z-10 grid h-full grid-cols-2 place-items-center">
                <span
                  className={
                    mapType === "roadmap" ? "text-white" : "text-[#374049]"
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="block h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6" />
                    <line x1="9" y1="3" x2="9" y2="18" />
                    <line x1="15" y1="6" x2="15" y2="21" />
                  </svg>
                </span>

                <span
                  className={
                    mapType === "satellite" ? "text-white" : "text-[#374049]"
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="block h-4 w-4 translate-y-px"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M13 7 9 3 5 7l4 4" />
                    <path d="m17 11 4 4-4 4-4-4" />
                    <path d="M8 12 3 17" />
                    <path d="M16 6 21 1" />
                    <path d="m9 11 4 4" />
                  </svg>
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={useCurrentLocation}
              disabled={locating}
              title="Use my location"
              aria-label="Use my location"
              className="w-10 h-10 rounded-full bg-white border border-[#93ca87]/40 text-[#24c3be] shadow-md hover:bg-[#24c3be] hover:text-white active:scale-95 transition disabled:opacity-60 flex items-center justify-center"
            >
              {locating ? "…" : "◎"}
            </button>
          </div>
        </div>
        <div className="shrink-0 border border-[#374049]/15 bg-[#374049] p-3 shadow-sm">
          <div className="rounded-xl bg-white px-4 py-3">
            <div className="text-[#24c3be] text-xs mb-1 font-semibold">
              Selected location
            </div>
            <div className="font-medium text-[#374049]">
              {selectedLocation ? selectedLocation.name : "—"}
            </div>
          </div>

          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="grid grid-cols-2 gap-2 w-full sm:w-64">
              <div className="h-10 flex items-center bg-white border border-white/20 rounded-xl px-3 text-xs">
                <span className="text-[#24c3be] font-semibold">Lat: </span>
                <span className="font-medium text-[#374049]">
                  {hasValidCoordinates(selectedLocation)
                    ? Number(selectedLocation.lat).toFixed(5)
                    : "—"}
                </span>
              </div>

              <div className="h-10 flex items-center bg-white border border-white/20 rounded-xl px-3 text-xs">
                <span className="text-[#24c3be] font-semibold">Lng: </span>
                <span className="font-medium text-[#374049]">
                  {hasValidCoordinates(selectedLocation)
                    ? Number(selectedLocation.lng).toFixed(5)
                    : "—"}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="h-10 border border-white/20 bg-white text-[#374049] px-5 rounded-xl text-sm hover:bg-[#24c3be]/10 transition"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  if (hasValidCoordinates(selectedLocation)) {
                    onConfirm(selectedLocation);
                  }
                }}
                disabled={!hasValidCoordinates(selectedLocation)}
                className="h-10 bg-[#24c3be] text-white px-6 rounded-xl text-sm hover:bg-[#1faeaa] transition disabled:opacity-50"
              >
                Use Location
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationPickerModal;
